
/**
 * @fileOverview ContentSliderController This creates a slider widget that allows the user to click and drag an indicator
 * along a bar, that gesture translates to a movement command that can be sent to a view that has content to be updated.
 *
 * It is expected that the view has an interface that includes an updatePosition() method that accepts a number and has
 * a isActive() method that returns a boolean that indicates if the view is currently active.
 *
 * NOTE: THIS REQUIRE MODULE IS NAME SPACED, AND NEEDS TO HAVE ITS WRAPPER FUNCTION REMOVED IN ORDER TO REUSE ELSEWHERE *
 *
 *
 * @version 1.4.4
 */

(function (context) {
    'use strict';

    var require = context.require;
    var requirejs = context.requirejs;
    var define = context.define;

    define('controllers/ContentSliderController',['require','jquery','lib/Util','GlobalEventDispatcher'],function (require) {

        var $ = require('jquery');
        var Util = require('lib/Util');
        var GlobalEventDispatcher = require('GlobalEventDispatcher');

        /**
         * Description
         *
         * @name ContentSliderController
         * @class ContentSliderController short description
         * @constructor
         * @param {String} sliderSelector
         * @param {ContentSliderView} contentView
         *
         * @since 1.0
         */
        var ContentSliderController = function (sliderSelector, contentView) {

            /**
             * The slider element itself.
             *
             * @type {jQuery|HTMLElement}
             */
            this.$slider = $(sliderSelector);

            /**
             * The global instance of an event dispatcher, used to subscribe to or publish events of global interest
             *
             * @type {EventDispatcher}
             */
            this.eventDispatcher = GlobalEventDispatcher.getEventDispatcher();

            /**
             * The view that will be updated, has a interface that includes
             * an isActive() and updatePosition() method.
             *
             * @type {ContentSliderView}
             */
            this.view = contentView;

            this.init();
        };

        /**
         * Initializes the UI Component View
         * Runs a single setupHandlers call, followed by createChildren and layout
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.init = function () {
            this.isEnabled = false;
            return this.setupHandlers()
                .createChildren()
                .layout()
                .enable()
                .resetSlider();
        };

        /**
         * Binds the scope of any handler functions
         * Should only be run on initialization of the view
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.setupHandlers = function () {
            // Bind event handlers scope here
            this.onClickHandler = this.onClick.bind(this);
            this.onDragStartHandler = this.onDragStart.bind(this);

            //subscribe to events using the global event dispatchers built in object for tracking available events
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.WINDOW_RESIZE, this.resetSlider.bind(this));
            this.eventDispatcher.subscribe(GlobalEventDispatcher.EVENTS.CONTENT_UPDATED, this.resetSlider.bind(this));

            return this;
        };

        /**
         * Create any child objects or references to DOM elements
         * Should only be run on initialization of the view
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.createChildren = function () {
            this.$sliderIndicator = this.$slider.next('.js-sliderIndicator');

            return this;
        };

        /**
         * Performs measurements and applies any positioning style logic
         * Should be run anytime the parent layout changes
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.layout = function () {
            this.sliderWidth = this.$slider.width();
            this.indicatorWidth = this.$sliderIndicator.width();

            return this;
        };

        /**
         * Enables the view
         * Performs any event binding to handlers
         * Exits early if it is already enabled
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.enable = function () {
            if (this.isEnabled) {
                return this;
            }

            $debug('ContentSliderController.js loaded/enabled');
            this.isEnabled = true;

            // moves the indicator to where you clicked on the slider
            this.$slider.on('mousedown.ContentSliderController', this.onClickHandler);

            //on mousedown or touchstart, start the dragging behavior
            this.$sliderIndicator.on('mousedown.ContentSliderController', this.onDragStartHandler);
            this.$sliderIndicator.on('touchstart', this.onDragStartHandler);

            return this;
        };

        /**
         * Disables the view
         * Tears down any event binding to handlers
         * Exits early if it is already disabled
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.disable = function () {
            if (!this.isEnabled) {
                return this;
            }

            this.isEnabled = false;

            return this;
        };

        /**
         * Destroys the view
         * Tears down any events, handlers, elements
         * Should be called when the object should be left unused
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.destroy = function () {
            this.disable();

            return this;
        };

        /**
         * Resets the slider position, sets global width variable, and checks to see if the view is active and sets
         * the slider state accordingly.
         *
         * @returns {ContentSliderController}
         * @since 1.0
         */
        ContentSliderController.prototype.resetSlider = function () {
            this.$sliderIndicator.css('left', 0);
            this.sliderWidth = this.$slider.width();

            if (this.view.isActive() === false) {
                this.$sliderIndicator.removeClass('MMM--isActive');
                this.disable();
            } else {
                this.$sliderIndicator.addClass('MMM--isActive');
                this.enable();
            }

            return this;

        };

        /**
         * Used for the click and slider events, calculates if there is a need for a page offset, occurs when the window
         * width is greater than 1440px.
         *
         * @return {Number}
         */
        ContentSliderController.prototype.getPageOffset = function () {
            /**
             * The page offset is used so that we can get the right number when in a view greater than 1440px
             * @type {Number}
             */
            var pageOffset = 41;

            /**
             * The width of the window.
             *
             * @type {Number}
             */
            var windowWidth = $(window).innerWidth();

            if (windowWidth > 1440) {
                pageOffset = ((windowWidth - 1440) / 2) + 41;
            }

            return pageOffset;
        };

        /**
         * Utility method used to disable temporarily disable text selection in IE 8
         */
        ContentSliderController.prototype.disableTextSelect = function () {
            document.body.onselectstart = function () {
                return false;
            };

            // Disable dragging.
            document.body.ondragstart = function () {
                return false;
            };
        };

        /**
         * Utility method used to enable text selection in IE 8 after a drag event has completed.
         */
        ContentSliderController.prototype.enableTextSelect = function () {
            document.body.onselectstart = function () {
                return true;
            };

            // Disable dragging.
            document.body.ondragstart = function () {
                return true;
            };
        };

        /**
        * onClick Handler
        * Performs this action on click
        *
        * @param {MouseEvent} event Click event
        * @since 1.0
        */
        ContentSliderController.prototype.onClick = function (event) {
            if (!this.isEnabled) {
                return;
            }

            /**
             * Calculates an offset for the page, if needed.
             * @type {Number}
             */
            var pageOffset = this.getPageOffset();

            var location = 0;
            /**
             * Sets the maximum offset the indicator can be animated to
             * @type {Number}
             */
            var maxIndicatorPosition = (this.$slider.outerWidth() - this.indicatorWidth);

            var xPosition = (event.pageX - pageOffset);

            //needed for IE Support
            if (event.preventDefault) {
                event.preventDefault();
            } else {
                event.returnValue = false;
            }

            if (xPosition < 0) {
                xPosition = 0;
            }

            if (xPosition > maxIndicatorPosition) {
                xPosition = maxIndicatorPosition;
            }

            var hitLoc = (event.pageX - pageOffset);

            if (hitLoc < 0) {
                hitLoc = 0;
            }

            this.$sliderIndicator.css('left', xPosition);

            location = (hitLoc / this.sliderWidth);
            this.view.updatePosition((location));
        };

        /**
         * Handles the dragging events, binds an event to mousemove until the mouseup or mouseleave events trigger
         * @param event
         */
        ContentSliderController.prototype.onDragStart = function (event) {
            if (!this.isEnabled) {
                return;
            }
            var self = this;

            //needed for IE Support
            if (event.preventDefault) {
                event.preventDefault();
            } else {
                event.returnValue = false;
            }

            /**
             * Calculates an offset for the page, if needed.
             * @type {Number}
             */
            var pageOffset = this.getPageOffset();

            /**
             * In order to create a buffer for the indicator, we need to take 1/2 its width and the padding
             *
             * @type {Number}
             */
            var indicatorBuffer = ((this.indicatorWidth / 2) + 22);

            /**
             * Sets the maximum offset the indicator can be animated to
             * @type {Number}
             */
            var maxIndicatorPosition = (this.$slider.outerWidth() - this.indicatorWidth); // the max position the indicator can move to

            //adds an active class to teh slider, should it be needed for styling
            this.$slider.addClass('MMM--isActive');

            var movementTracker = function (event) {
                //needed for IE Support
                if (event.preventDefault) {
                    event.preventDefault();
                } else {
                    event.returnValue = false;
                }
                //determine the mouse position, subtract page buffer from the mouseX to determine distance from left of slider
                var xPosition = (event.pageX  - pageOffset);

                var carouselPosition = xPosition / self.sliderWidth;
                //limit the xPosition to being within the bounds of the slider
                if ((xPosition) > maxIndicatorPosition) {
                    xPosition = maxIndicatorPosition;
                    carouselPosition = 1;
                } else if (xPosition < 0) {
                    xPosition = 0;
                    carouselPosition = 0;
                }

                //move the indicator to where the mousedown event happened
                self.$sliderIndicator.css('left', xPosition);
                //send a message to update the view based on where you clicked relative to the width of the slider
                self.view.updatePosition(carouselPosition);

                self.disableTextSelect();

            };

            if (event.type === 'mousedown') {

                var removeMouseListener = function (event) {
                    $(document).off('mouseup.ContentSliderController');
                    $(document).off('mousemove.ContentSliderController', movementTracker); //remove the listener when finally let up the mouse
                    self.enableTextSelect();
                };

                //bind the movementTracker to the mousemove event
                $(document).on('mousemove.ContentSliderController', movementTracker);
                $(document).on('mouseup.ContentSliderController', removeMouseListener);


            } else if (event.type === 'touchstart') {
                var removeTouchMoveListener = function (event) {
                    $(document).off('touchend.ContentSliderController');
                    $(document).off('touchmove.ContentSliderController', movementTracker); //remove the listener when finally let up the mouse
                    self.enableTextSelect();
                };

                //bind the movementTracker to the mousemove event
                $(document).on('touchmove.ContentSliderController', movementTracker);
                $(document).on('touchend.ContentSliderController', removeTouchMoveListener);
            }
        };

        return ContentSliderController;
    });

}(MMMRequire));

